import React, { useCallback, useMemo, useRef } from 'react'
import * as Yup from 'yup'
import { Box, Button, ToggleButton } from '@mui/material'
import { withFeatures } from 'features'
import { Field, FormikConfig, FormikHelpers, FormikProps, FormikValues } from 'formik'
import { ToggleButtonGroup } from 'formik-mui'
import { History } from 'history'
import { withNavigation, WithNavigationProps } from 'hocs'
import { InventoryServiceContext } from 'inventory/context'
import { InventoryItem, InventoryRule } from 'inventory/inventoryService'
import { Link } from 'react-router-dom'
import { FormWrapper } from 'uiComponents/form/form'
import { FormItemName } from 'uiComponents/form/formElements'
import FieldWrapper from 'uiComponents/input/fieldWrapper'
import RadioFieldFormik from 'uiComponents/input/radio/radioFieldFormik'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import { ChartDataLoader, TableLoader } from 'uiComponents/loaders'
import { MessageKind } from 'uiComponents/messages'
import { TextWithHorizontalLine } from 'uiComponents/pageElements'
import { NoResultsRow } from 'uiComponents/table/noResultsRow'
import { Caption } from 'uiComponents/typography'
import { addSeparators, delay } from 'utils'
import { RulesList } from './rulesList'
import { CrowdControlFormState } from './types'
import PeopleDoorSolid from '@convious/icons/src/icons/solid/peopleDoorSolid.svg'
import PeopleRoofSolid from '@convious/icons/src/icons/solid/peopleRoofSolid.svg'
import PeopleDoorDuotone from '@convious/icons/src/icons/duotone/peopleDoorDuotone.svg'
import PeopleRoofDuotone from '@convious/icons/src/icons/duotone/peopleRoofDuotone.svg'
import { Body } from 'uiComponents/typography'
import './crowdControl.scss'
import { t, Trans, useTranslation } from 'translations'

const InventoryFormSchema = Yup.object().shape({
    name: Yup.string().required().max(500),
    limitType: Yup.string().required(),
    isLimited: Yup.boolean(),
    limit: Yup.mixed().when('isLimited', {
        is: true,
        then: Yup.number().typeError(() => t('common.validation.required')).required().min(0).max(100000),
        otherwise: Yup.mixed().optional(),
    }),
})
const getLimitInfo = () => ({
    capacity: {
        title: t('inventory.crowdControl.capacityLimit'),
        description: t('inventory.crowdControl.capacityLimitDescription'),
        Icon: PeopleRoofDuotone,
    },
    admission: {
        title: t('inventory.crowdControl.admissionLimit'),
        description: t('inventory.crowdControl.admissionLimitDescription'),
        Icon: PeopleDoorDuotone,
    },
})

const Summary: React.FC<CrowdControlFormState> = ({ name, isLimited, limit, limitType }) => {
    const { t } = useTranslation()
    const activityName = name || t('inventory.crowdControl.summary.activityNameFallback')
    const limitDescription = isLimited
        ? limit != null
            ? `${addSeparators(limit)}`
            : t('inventory.crowdControl.summary.inputNumber')
        : t('inventory.crowdControl.unlimited')

    return (
        <p>
            {limitType === 'capacity' ? (
                <Trans
                    i18nKey="inventory.crowdControl.summary.capacity"
                    values={{ limitDescription, activityName }}
                    components={{ strong: <strong /> }}
                />
            ) : (
                <Trans
                    i18nKey="inventory.crowdControl.summary.admission"
                    values={{ limitDescription, activityName }}
                    components={{ strong: <strong /> }}
                />
            )}
        </p>
    )
}

interface CrowdControlFormParams {
    accountSlug: string
    id: string
}

interface CrowdControlFormProps extends WithNavigationProps<CrowdControlFormParams> {
    accountSlug: string
    history: History
    setActiveSection: (section: string, header: string) => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
    hasFeature: (feature: string, accountSlug: string) => boolean
}

const CrowdControlForm: React.FC<CrowdControlFormProps> = ({
    accountSlug,
    history,
    match,
    hasFeature,
    replaceMessages,
    setActiveSection,
    removeAllMessages,
}) => {
    const { t } = useTranslation()
    const inventoryService = React.useContext(InventoryServiceContext)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [updating, setUpdating] = React.useState<boolean>(false)
    const [rulesList, setRulesList] = React.useState<InventoryRule[]>([])
    const formikRef = useRef<FormikProps<FormikValues>>(null)

    const initialValues: CrowdControlFormState = React.useMemo(
        () => ({ name: '', limitType: 'capacity', isLimited: true, limit: undefined }),
        [],
    )

    const fetchInventoryData = async (id: string) => {
        try {
            setLoading(true)
            const data = await inventoryService.getInventoryDetails(accountSlug, id)
            initialValues.name = data.name
            initialValues.limit = data.defaultAvailability
            initialValues.isLimited = data.defaultAvailability !== null
            initialValues.limitType = data.reservationPeriodType === 'Venue' ? 'capacity' : 'admission'
            setRulesList(data.availabilityRules)
        } catch {
            replaceMessages('server_error', 'error', t('inventory.crowdControl.fetchError'))
        } finally {
            setLoading(false)
        }
    }

    const inventoryId = useMemo(() => match.params.id, [match.params.id])

    React.useEffect(() => {
        setActiveSection('inventory', t('inventory.crowdControl.title'))
        if (!!inventoryId && inventoryId !== 'new') {
            fetchInventoryData(inventoryId)
        }
    }, [inventoryId])

    const onInventorySave: FormikConfig<CrowdControlFormState>['onSubmit'] = async (values, { validateForm }) => {
        const { name, limit, isLimited, limitType } = values
        const formErrors = await validateForm()

        if (formErrors && Object.keys(formErrors).length > 0) {
            return
        }

        try {
            setUpdating(true)
            const payload: InventoryItem = {
                id: match.params.id,
                name,
                account: accountSlug,
                defaultAvailability: isLimited ? limit : null,
                reservationPeriodType: limitType === 'capacity' ? 'Venue' : 'Gate',
            }
            if (match.params.id !== 'new') {
                await inventoryService.updateInventoryItem(accountSlug, payload)
            } else {
                const savedInventory = await inventoryService.createInventoryItem(accountSlug, payload)
                const id = savedInventory.id
                history.push(`/account/${accountSlug}/products/inventory/item/${id}`)
            }
            setUpdating(false)
            replaceMessages('success', 'success', t('inventory.crowdControl.saveSuccess'))
            formikRef.current?.resetForm({ values: formikRef.current.values })

            await delay(3000)
            removeAllMessages()
        } catch {
            setUpdating(false)
            replaceMessages(
                'server_error',
                'error',
                t('inventory.crowdControl.saveError'),
            )
        }
    }

    const handleLimitTypeChange = (
        value: CrowdControlFormState['limitType'],
        setFieldValue: FormikHelpers<CrowdControlFormState>['setFieldValue'],
    ) => {
        if (value !== null) {
            setFieldValue('limitType', value)
        }
    }

    const isNew = useMemo(() => inventoryId === 'new', [inventoryId])

    const renderLimitType = useCallback(() => {
        if (isNew && formikRef.current) {
            const { setFieldValue } = formikRef.current

            return (
                <FieldWrapper label={t('inventory.crowdControl.limitTypeLabel')} status="normal">
                    <Field
                        component={ToggleButtonGroup}
                        name="limitType"
                        className="iconToggles"
                        type="checkbox"
                        exclusive
                    >
                        <ToggleButton
                            value="capacity"
                            aria-label="capacity-limit"
                            size="medium"
                            disabled={!isNew}
                            onChange={() => handleLimitTypeChange('capacity', setFieldValue)}
                        >
                            <PeopleRoofSolid className="crowd-control-limit-type-icon" />
                            {t('inventory.crowdControl.capacityLimit')}
                        </ToggleButton>
                        <ToggleButton
                            value="admission"
                            aria-label="admission-limit"
                            size="medium"
                            disabled={!isNew}
                            onChange={() => handleLimitTypeChange('admission', setFieldValue)}
                        >
                            <PeopleDoorSolid className="crowd-control-limit-type-icon" />
                            {t('inventory.crowdControl.admissionLimit')}
                        </ToggleButton>
                    </Field>
                </FieldWrapper>
            )
        }
        return (
            <FieldWrapper label={t('inventory.crowdControl.limitTypeLabel')} status="normal">
                <Body color="light">
                    <Trans
                        i18nKey="inventory.crowdControl.limitTypeLocked"
                        components={{ anchor: <a href="mailto:support@convious.com" /> }}
                    />
                </Body>
            </FieldWrapper>
        )
    }, [isNew, handleLimitTypeChange])

    return (
        <>
            {loading && <TableLoader style={{ minHeight: '41em' }} />}
            {updating && <ChartDataLoader />}
            {!loading && (
                <>
                    <FormWrapper
                        enableReinitialize
                        validateOnChange
                        initialValues={initialValues}
                        onSubmit={onInventorySave}
                        validationSchema={InventoryFormSchema}
                        className="crowd-control"
                        innerRef={formikRef}
                    >
                        {({ values, setFieldValue, setFieldError, dirty }) => {
                            const { title, description, Icon } = getLimitInfo()[values.limitType]

                            return (
                                <>
                                    <section className="crowd-control-left">
                                        <TextInputFieldFormik
                                            id="name"
                                            name="name"
                                            label={t('inventory.crowdControl.nameLabel')}
                                            placeholder={t('inventory.crowdControl.namePlaceholder')}
                                        />

                                        {renderLimitType()}

                                        <Box>
                                            <FormItemName weight="medium" size={1} className="label">
                                                {t('inventory.crowdControl.defaultLimitLabel')}
                                            </FormItemName>
                                            <div className="crowd-control-radio">
                                                <RadioFieldFormik
                                                    checkMarkSize="small"
                                                    name="isLimited"
                                                    label={t('inventory.crowdControl.unlimited')}
                                                    checked={!values.isLimited}
                                                    onChange={() => {
                                                        setFieldError('limit', undefined)
                                                        setFieldValue('isLimited', false)
                                                    }}
                                                />
                                                <RadioFieldFormik
                                                    checkMarkSize="small"
                                                    name="isLimited"
                                                    label={t('inventory.crowdControl.limitOf')}
                                                    checked={values.isLimited}
                                                    onChange={() => {
                                                        setFieldValue('isLimited', true)
                                                    }}
                                                />
                                                <TextInputFieldFormik
                                                    id="limit"
                                                    name="limit"
                                                    type="number"
                                                    placeholder={t('inventory.crowdControl.limitPlaceholder')}
                                                    className="crowd-control-limit-input"
                                                    disabled={!values.isLimited}
                                                    validateOnChange
                                                />
                                            </div>
                                        </Box>
                                        <div className="crowd-control-left-buttons">
                                            <Button
                                                component={Link}
                                                to={`/account/${accountSlug}/products/inventory/home`}
                                                variant="outlined"
                                                size="large"
                                            >
                                                {t('common.cancel')}
                                            </Button>

                                            <Button type="submit" variant="contained" size="large" disabled={!dirty}>
                                                {t('common.save')}
                                            </Button>
                                        </div>
                                    </section>
                                    <section className="crowd-control-right">
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            <Icon className="crowd-control-right-icon" />
                                            <h5>{title}</h5>
                                        </Box>
                                        <p className="crowd-control-right-text">{description}</p>
                                        <div className="crowd-control-right-summary">
                                            <h6>{t('inventory.crowdControl.howItWorks')}</h6>
                                            <Summary
                                                name={values.name}
                                                isLimited={values.isLimited}
                                                limit={values.limit}
                                                limitType={values.limitType}
                                            />
                                        </div>
                                        <Caption color="light" className="caption">
                                            {t('inventory.crowdControl.unlessExceptions')}
                                        </Caption>
                                    </section>
                                </>
                            )
                        }}
                    </FormWrapper>
                    {match.params.id !== 'new' && (
                        <Box padding="2rem" paddingTop="0" display="flex" flexDirection="column" alignItems="center">
                            <TextWithHorizontalLine text={t('inventory.crowdControl.exceptionsHeading')} alignment="left" />
                            {rulesList.length > 0 && (
                                <Box marginBottom={2} width="100%">
                                    <RulesList
                                        accountSlug={accountSlug}
                                        rulesList={rulesList}
                                        inventoryId={match.params.id}
                                        hasFeature={hasFeature}
                                    />
                                </Box>
                            )}
                            {rulesList.length === 0 && <NoResultsRow text={t('inventory.crowdControl.noExceptions')} />}
                            <div>
                                <Button
                                    component={Link}
                                    to={`/account/${accountSlug}/products/inventory/rule/new/item/${match.params.id}`}
                                    variant="text"
                                    size="large"
                                    id="newRule"
                                >
                                    {t('inventory.crowdControl.addException')}
                                </Button>
                            </div>
                        </Box>
                    )}
                </>
            )}
        </>
    )
}

export default withFeatures(withNavigation(CrowdControlForm))
